<template>
    <modal-error
        :is-open="is_open"
        @close="close"
    >
        <template v-if="$error.is(error, 401)">
            <template slot="heading">401 Unauthorized</template>

            <p>
                Perhaps you've been inactive for a while and your session
                has expired. Regardless, please log back in.
            </p>

            <template slot="footer">
                <div class="text-right">
                    <laroute-link
                        class="w-full text-center btn btn-red-lightest md:w-auto"
                        :to="{ name: 'auth.sessions.create' }"
                    >
                        Login
                    </laroute-link>
                </div>
            </template>
        </template>

        <template v-else-if="$error.is(error, 403)">
            <template slot="heading">403 Forbidden</template>

            <p>
                You're not authorized to do that. If you think there
                has been a mistake, try logging out and back in again.
            </p>

            <template slot="footer">
                <div class="flex flex-col md:flex-row-reverse">
                    <router-link
                        class="w-full text-center btn btn-red-lightest md:w-auto"
                        :to="{ name: $router.fix('dashboard.*.overview.index') }"
                    >
                        Go To Overview
                    </router-link>
                    <laroute-link
                        :to="{ name: 'auth.sessions.destroy' }"
                        class="text-center btn md:mr-3"
                    >
                        Logout
                    </laroute-link>
                </div>
            </template>
        </template>

        <template v-else-if="$error.is(error, 404)">
            <template slot="heading">404 Not Found</template>

            <p>
                Not sure how you got here, or even where here is.
            </p>

            <template slot="footer">
                <div class="text-right">
                    <router-link
                        class="w-full text-center btn btn-red-lightest md:w-auto"
                        :to="{ name: $router.fix('dashboard.*.overview.index') }"
                    >
                        Go To Overview
                    </router-link>
                </div>
            </template>
        </template>

        <template v-else-if="$error.is(error, 408)">
            <template slot="heading">408 Request Timeout</template>

            <p class="mb-3">
                Apologies for the inconvenience. It appears our servers are having
                a tough time at the moment. We're more than likley aware of the issue
                and are fixing it right now.
            </p>

            <p>Please try again later.</p>

            <template slot="footer">
                <div class="text-right">
                    <button
                        v-if="canClose"
                        class="text-center btn btn-red-lightest md:mr-3"
                        type="button"
                        @click.prevent="close"
                    >
                        Close
                    </button>
                </div>
            </template>
        </template>

        <template v-else-if="$error.is(error, 413)">
            <template slot="heading">413 Payload Too Large</template>

            <p>
                We can't handle your request because it's too large. Perhaps you've
                uploaded a file that's too large. Try changing the file type
                or cropping/resizing it if it's an image.
            </p>

            <template slot="footer">
                <div class="text-right">
                    <button
                        v-if="canClose"
                        class="text-center btn btn-red-lightest md:mr-3"
                        type="button"
                        @click.prevent="close"
                    >
                        Close
                    </button>
                </div>
            </template>
        </template>

        <template v-else-if="$error.is(error, 429)">
            <template slot="heading">429 Too Many Requests</template>

            <p class="mb-3">
                Hold your horses. You're trying to do too much too fast. To
                help prevent
                <a
                    href="https://en.wikipedia.org/wiki/Denial-of-service_attack"
                    class="no-underline text-blue"
                >
                    DDoS attacks,
                </a>
                we limit how many things you can do in a given time.
                It appears you've hit that limit.
            </p>

            <p>Please wait a few moments before trying again.</p>

            <template slot="footer">
                <div class="text-right">
                    <button
                        v-if="canClose"
                        class="text-center btn btn-red-lightest md:mr-3"
                        type="button"
                        @click.prevent="close"
                    >
                        Close
                    </button>
                </div>
            </template>
        </template>

        <template v-else-if="$error.is(error, 503)">
            <template slot="heading">503 Service Unavailable</template>

            <p class="mb-3">
                Apologies for the inconvenience. We're doing some maintenance.
            </p>

            <p>Please try again later.</p>

            <template slot="footer">
                <div class="text-right">
                    <button
                        v-if="canClose"
                        class="text-center btn btn-red-lightest md:mr-3"
                        type="button"
                        @click.prevent="close"
                    >
                        Close
                    </button>
                </div>
            </template>
        </template>

        <template v-else-if="$error.is(error, 511)">
            <template slot="heading">511 Network Authentication Required</template>

            <p>
                You can't do that right now as it appears you're not authenticated
                on your network. This usually happens on public wifi/networks - for example in an internet café or at an airport.
            </p>

            <template slot="footer">
                <div class="text-right">
                    <button
                        v-if="canClose"
                        class="text-center btn btn-red-lightest md:mr-3"
                        type="button"
                        @click.prevent="close"
                    >
                        Close
                    </button>
                </div>
            </template>
        </template>

        <template v-else>
            <template slot="heading">Something has gone wrong.</template>

            <p>
                We're sorry for the inconvenience. Something unexpected has happened.
                Our team have been notified, and will fix this error as soon as possible
            </p>

            <template slot="footer">
                <div class="flex flex-col md:flex-row-reverse">
                    <slot name="primary-action" />
                    <button
                        :class="{
                            'btn-red-lightest': !$slots['primary-action']
                        }"
                        class="btn md:mr-3"
                        type="button"
                        @click.prevent="close"
                    >
                        Close
                    </button>
                </div>
            </template>
        </template>
    </modal-error>
</template>

<script>
    import ModalError from './modal-error'

    export default {
        components: {
            ModalError
        },
        props: {
            error: {
                type: Error,
                required: true
            },
            canClose: {
                type: Boolean,
                default: true
            }
        },
        data () {
            return {
                is_open: true
            }
        },
        created () {
            console.error(this.error)
        },
        methods: {
            close () {
                if (!this.canClose || this.$error.is(this.error, [401, 403])) {
                    return
                }

                this.is_open = false

                this.$emit('close')
            }
        }
    }
</script>
